import type {Component, Editor} from 'grapesjs';
import type {AiRedesignPromptBoxProps, RedesignIssue, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent, PromptOption, PromptSuggestions, RedesignIssueType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {useAppSelector} from '@/hooks';
import {
  addLoadingComponent,
  addRewriteIssue,
  getRewriteIssue,
  isComponentLoading,
  removeLoadingComponent,
  removeRewriteIssue,
} from '@Components/email-maker-ai-rewrite-prompt-box/email-maker-ai-rewrite-prompt-box.reducer';
import type {EmailCampaignAiRewriteSource} from '@Libraries/ai-email-campaign-content-library';
import {getAiContentErrorMessage} from '@Libraries/ai-email-campaign-content-library';
import {
  EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS,
  EMAIL_MAKER_AI_REWRITE_LOADING_TOAST,
  EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT,
  EMAIL_MAKER_AI_REWRITE_PROMPT,
  EMAIL_MAKER_AI_REWRITE_SINGUP_SOURCE,
} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import {getUniqueId} from '@Utils/string.util';
import type {ErrorType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextErrorTypes} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiRedesignPromptBoxIssue} from '@Libraries/ai-redesign-prompt-box-library';
import {getErrorMessageFromCode} from '@Libraries/ai-text-prompt-box-library';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {onAiRedesignPromptSuggestionSelected, onAiRedesignRecentPromptSelected} from '@Components/poster-editor/library/poster-editor-ai-redesign-library';

export const EMAIL_CAMPAIGN_AI_REWRITE_COST = 1;

function getRewriteIssueIconFromCode(code: AiTextErrorTypes): string {
  switch (code) {
    case AiTextErrorTypes.LowCredits:
      return 'icon-credits';
    default:
      return 'icon-exclamation-triangle';
  }
}

function getRewriteIssueTypeFromCode(code: AiTextErrorTypes): RedesignIssueType {
  switch (code) {
    case AiTextErrorTypes.LowCredits:
      return RedesignIssueType.WARNING;
    default:
      return RedesignIssueType.ERROR;
  }
}

function getRewriteIssueIcon(error: ErrorType): string {
  const {code} = error;
  return getRewriteIssueIconFromCode(code as AiTextErrorTypes);
}

function getRewriteIssueType(error: ErrorType): RedesignIssueType {
  const {code} = error;
  return getRewriteIssueTypeFromCode(code as AiTextErrorTypes);
}

export function getSelectedComponentsPromptBoxIssue(selectedComponent: Component, usageSource: EmailCampaignAiRewriteSource): RedesignIssue | undefined {
  return useAppSelector((state) => {
    return getRewriteIssue(state, selectedComponent.ccid, usageSource);
  });
}

export function addSelectedComponentToLoadingComponents(selectedComponent: Component): void {
  window.PMW.redux.store.dispatch(addLoadingComponent(selectedComponent.ccid));
}

export function removeSelectedComponentFromLoadingComponents(selectedComponent: Component): void {
  window.PMW.redux.store.dispatch(removeLoadingComponent(selectedComponent.ccid));
}

export function isSelectedComponenetsPromptBoxLoading(selectedComponent: Component): boolean {
  return useAppSelector((state) => {
    return isComponentLoading(state, selectedComponent.ccid);
  });
}

export function addSelectedComponentsRewriteIssue(selectedComponent: Component, usageSource: EmailCampaignAiRewriteSource, issueContent: RedesignIssue): void {
  window.PMW.redux.store.dispatch(
    addRewriteIssue({
      componentId: selectedComponent.ccid,
      source: usageSource,
      issue: issueContent,
    })
  );
}

export function removeSelectedComponentsRewriteIssue(selectedComponent: Component): void {
  window.PMW.redux.store.dispatch(removeRewriteIssue(selectedComponent.ccid));
}

export function getPromptBoxPrompt(): string | undefined {
  const storedPrompt = sessionStorage.getItem(EMAIL_MAKER_AI_REWRITE_PROMPT);

  if (storedPrompt) {
    return JSON.parse(storedPrompt) as string;
  }

  return undefined;
}

export function setPromptBoxPrompt(prompt: string): void {
  sessionStorage.setItem(EMAIL_MAKER_AI_REWRITE_PROMPT, JSON.stringify(prompt));
}

export function getPromptOptionValues(): SelectedPromptOptionValues | undefined {
  const storedOptions = sessionStorage.getItem(EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS);
  if (storedOptions) {
    return JSON.parse(storedOptions) as SelectedPromptOptionValues;
  }

  return undefined;
}

export function setPromptOptionValues(options: SelectedPromptOptionValues): void {
  sessionStorage.setItem(EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS, JSON.stringify(options));
}

export function getPromptBoxPreviousPrompt(): string | undefined {
  const storedPrompt = sessionStorage.getItem(EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT);

  if (storedPrompt) {
    return JSON.parse(storedPrompt) as string;
  }

  return undefined;
}

export function setPromptBoxPreviousPrompt(prompt: string): void {
  sessionStorage.setItem(EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT, JSON.stringify(prompt));
}

function handleRewriteError(error: ErrorType, selectedComponent: Component, usageSource: EmailCampaignAiRewriteSource): never {
  const message = getAiContentErrorMessage(error);
  let issueContent: RedesignIssue;

  if (!message) {
    issueContent = {
      type: getRewriteIssueTypeFromCode(AiTextErrorTypes.GeneralError),
      icon: getRewriteIssueIconFromCode(AiTextErrorTypes.GeneralError),
      message: getErrorMessageFromCode(AiTextErrorTypes.GeneralError),
    };
  } else {
    issueContent = {
      type: getRewriteIssueType(error),
      icon: getRewriteIssueIcon(error),
      message,
    };
  }

  addSelectedComponentsRewriteIssue(selectedComponent, usageSource, issueContent);
  throw new AiRedesignPromptBoxIssue(issueContent.message, issueContent.icon, issueContent.type);
}

export async function executeRewriteSelectedComponent(
  editor: Editor,
  selectedComponent: Component,
  usageSource: EmailCampaignAiRewriteSource,
  rewriteIssue: RedesignIssue | undefined,
  prompt: string,
  selectedPromptOptionValues: SelectedPromptOptionValues
): Promise<void> {
  const loadingKey = EMAIL_MAKER_AI_REWRITE_LOADING_TOAST + selectedComponent.ccid;
  initiatePosterMorphLoading(loadingKey, 'pmwjs_rewriting_with_ai_ellipsis');
  if (rewriteIssue) removeSelectedComponentsRewriteIssue(selectedComponent);
  addSelectedComponentToLoadingComponents(selectedComponent);

  try {
    editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: true, component: selectedComponent});

    await editor.runCommand(window.PMW.emails.commands.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {
      prompt: prompt,
      selectedPromptOptionValues: selectedPromptOptionValues,
      usageSource: usageSource,
      contentKey: getUniqueId(),
    });

    setPromptBoxPreviousPrompt(prompt);
    setPromptBoxPrompt('');
  } catch (e) {
    const error = e as ErrorType;
    handleRewriteError(error, selectedComponent, usageSource);
  } finally {
    editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: selectedComponent});
    removeSelectedComponentFromLoadingComponents(selectedComponent);
    terminatePosterMorphLoading(loadingKey);
  }
}

export function getAiRewritePromptBoxProps(
  rewriteSelectedComponent: (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues) => Promise<void>,
  rewriteIssue: RedesignIssue | undefined,
  remainingCredits: number,
  promptBoxClass: string,
  editor: Editor
): AiRedesignPromptBoxProps {
  return {
    isPromptBoxSelectedOnRender: true,
    regenerateContent: rewriteSelectedComponent,
    editableComponentsMap: {
      [EditableComponent.TEXT]: true,
    },
    promptOptionsForEditableComponents: {
      [EditableComponent.TEXT]: [PromptOption.MOOD, PromptOption.EMOJI_USAGE],
    },
    onPromptChange: setPromptBoxPrompt,
    defaultPrompt: getPromptBoxPrompt(),
    onSelectedPromptOptionValuesChange: setPromptOptionValues,
    defaultSelectedPromptOptionValues: getPromptOptionValues(),
    promptSuggestions: PromptSuggestions.EMAIL_SINGLE_REWRITE,
    onPromptSuggestionSelected: onAiRedesignPromptSuggestionSelected,
    onRecentPromptSelected: onAiRedesignRecentPromptSelected,
    recentPrompt: getPromptBoxPreviousPrompt,
    upsellDialog: {
      dialogName: window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT as string,
      showDialog: !isUserPremium(),
    },
    redesignIssueOnRender: rewriteIssue,
    promptBoxClasses: promptBoxClass,
    creditsPillContent: {
      remainingCredits: remainingCredits,
      actionCost: EMAIL_CAMPAIGN_AI_REWRITE_COST,
    },
    placeholder: window.i18next.t('pmwjs_rewrite_this_for_my_coffee_shop_ellipsis'),
    authenticateModalContent: {
      showModal: !isUserLoggedIn(),
      modalProps: {
        fbPermissions: '',
        signupSource: EMAIL_MAKER_AI_REWRITE_SINGUP_SOURCE,
        onLoginSuccess: async (): Promise<void> => {
          await editor.runCommand(window.PMW.emails.commands.SAVE_TO_DB_AND_CACHE);
          window.location.reload();
        },
      },
    },
  };
}

export async function fetchAiRewriteRecentPrompt(): Promise<void> {
  try {
    if (!isUserLoggedIn() || !isUserPremium()) {
      return;
    }

    const prompt = (await window.PMW.readLocal('emailcampaignaicontent/getAiRedesignRewritePrompt')) as string | null;
    if (prompt) {
      sessionStorage.setItem(EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT, JSON.stringify(prompt));
    }
  } catch {
    return;
  }
}
